import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContactLists } from '@/hooks/useContactLists';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, List, Trash2, Users, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { safeFormat } from '@/lib/safeDate';
import { ptBR } from 'date-fns/locale';

export default function Lists() {
  const navigate = useNavigate();
  const { lists, isLoading, createList, isCreating, deleteList } = useContactLists();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await createList({ name: newName.trim(), description: newDescription.trim() || null });
      setShowCreate(false);
      setNewName('');
      setNewDescription('');
    } catch {
      // toast handled inside hook
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteList(deleteTarget.id);
      setDeleteTarget(null);
    } catch {
      // toast handled inside hook
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="flex-shrink-0 p-4 md:p-6 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Listas de Contatos</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Organize contatos e negociações em listas para campanhas e outbound.
              </p>
            </div>
            <Button onClick={() => setShowCreate(true)} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Nova Lista
            </Button>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
            </div>
          ) : lists.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <List className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-1">Nenhuma lista criada</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Crie sua primeira lista selecionando contatos ou negociações em massa.
              </p>
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Criar lista
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {lists.map((list) => (
                <div
                  key={list.id}
                  className="bg-white dark:bg-slate-800 rounded-xl border border-border shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                  onClick={() => navigate(`/listas/${list.id}`)}
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <h3 className="font-semibold text-foreground leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                        {list.name}
                      </h3>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(list);
                        }}
                        className="flex-shrink-0 p-1 rounded hover:bg-destructive/10 hover:text-destructive text-muted-foreground transition-colors opacity-0 group-hover:opacity-100"
                        title="Excluir lista"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {list.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {list.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Users className="w-4 h-4" />
                        <span>{list.item_count} {list.item_count === 1 ? 'item' : 'itens'}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <span>
                          {list.created_at ? safeFormat(new Date(list.created_at), "d MMM yyyy", { locale: ptBR }) : ''}
                        </span>
                        <ChevronRight className="w-3 h-3 group-hover:text-primary transition-colors" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nova Lista</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="create-name">Nome da lista *</Label>
              <Input
                id="create-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ex: Leads para campanha maio"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="create-desc">Descrição (opcional)</Label>
              <Textarea
                id="create-desc"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Descreva o objetivo desta lista..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={!newName.trim() || isCreating}>
              {isCreating ? 'Criando...' : 'Criar lista'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir lista "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Os contatos e negociações não serão excluídos, apenas removidos desta lista.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
